
import { useParams, useNavigate } from 'react-router-dom';
import { useBrgDetail } from '../../hooks/useBrgDetail';
import { useRoles } from '../../hooks/useRoles';
import { useAuth } from '../../auth/useAuth';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';
import { RoleDefinitionDto } from '../../types';

interface ReadOnlyFieldProps {
  label: string;
  value?: string | null;
  values?: string[]; // Support multiple values
}

function ReadOnlyField({ label, value, values }: ReadOnlyFieldProps) {
  // Determine display content - either single value or multiple values as chips
  const hasMultiple = values && values.length > 0;
  const displayValue = hasMultiple ? null : value;
  
  return (
    <div
      style={{
        marginBottom: '0',
      }}
    >
      <div
        style={{
          fontSize: '0.85rem',
          fontWeight: 500,
          color: '#000000',
          marginBottom: '6px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1rem',
          fontWeight: 600,
          color: (displayValue || hasMultiple) ? '#1a1a1a' : '#999',
          borderBottom: '1px solid #d0d0d0',
          paddingBottom: '8px',
          minHeight: hasMultiple ? 'auto' : undefined,
        }}
      >
        {hasMultiple ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {values.map((name, idx) => (
              <span
                key={idx}
                style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  backgroundColor: '#e3f0fb',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: '#1a365d',
                }}
              >
                {name}
              </span>
            ))}
          </div>
        ) : (
          displayValue || '—'
        )}
      </div>
    </div>
  );
}

export function BrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { roles: userRoles } = useAuth();
  const detailQuery = useBrgDetail(id);
  const rolesQuery = useRoles();

  const isAdmin = userRoles.includes('BRG.Admin');

  if (detailQuery.isLoading) {
    return <LoadingSpinner message="Loading BRG details..." fullPage />;
  }

  if (detailQuery.isError) {
    return (
      <div style={{ padding: '24px' }}>
        <ErrorMessage
          message={detailQuery.error?.message ?? 'Failed to load BRG.'}
          onRetry={() => void detailQuery.refetch()}
        />
      </div>
    );
  }

  const brg = detailQuery.data;
  if (!brg) return null;

  // Dynamic labels for main roles - from database
  const rolesDefs = rolesQuery.data ?? [];
  const assignments = brg.roleAssignments ?? [];

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
      {/* Action buttons */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: '32px' }}>
          <ActionButton
            label="EDIT"
            onClick={() => void navigate(`/admin/brgs/${id}/edit`)}
            primary
          />
          <ActionButton
            label="CALENDAR"
            onClick={() => void navigate(`/calendar`)}
            primary
          />
          <ActionButton
            label="ENGAGEMENT"
            onClick={() => void navigate(`/engagement`)}
            primary
          />
          <ActionButton
            label="BUDGET"
            onClick={() => void navigate(`/budget-dashboard`)}
            primary
          />
        </div>
      )}

      {/* Details Card */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '32px 40px',
          marginBottom: '24px',
        }}
      >
        {/* BRG Name */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#000000', marginBottom: '6px' }}>
            BRG Name
          </div>
          <h2 style={{ margin: '0', fontSize: '1.5rem', fontWeight: 600, color: '#1a1a1a', borderBottom: '1px solid #d0d0d0', paddingBottom: '8px' }}>
            {brg.name}
          </h2>
        </div>

        {/* BRG Description */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#000000', marginBottom: '6px' }}>
            BRG Description
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 600, color: brg.description ? '#1a1a1a' : '#999', borderBottom: '1px solid #d0d0d0', paddingBottom: '8px' }}>
            {brg.description || '—'}
          </div>
        </div>

        {/* Two-column grid for paired fields - dynamic from roles */}
        {(() => {
          const rolePairs: RoleDefinitionDto[][] = [];
          for (let i = 0; i < rolesDefs.length; i += 2) {
            rolePairs.push(rolesDefs.slice(i, i + 2));
          }
          return rolePairs.map((pair, pairIdx) => (
            <div
              key={pairIdx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px 32px',
                marginBottom: pairIdx < rolePairs.length - 1 ? '24px' : '0',
              }}
            >
              {pair.map((role) => {
                // Get ALL assignments for this role (supports multiple users per role)
                const roleAssignments = assignments.filter(a => a.roleId === role.roleId);
                const userNames = roleAssignments
                  .map(a => a.userName)
                  .filter((name): name is string => !!name);
                
                const label = role.roleCode
                  .replace(/^Co([A-Z])/, 'Co-$1')
                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                  .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

                // Dynamic label logic: same as form fields
                const isCoRoleFlag = /^Co[A-Z]/.test(role.roleCode) && role.roleCode !== 'CommunityLead';
                let displayLabel: string;
                if (isCoRoleFlag) {
                  // Co-role: show "(if any)" only when empty
                  displayLabel = userNames.length > 0 ? label : `${label} (if any)`;
                } else {
                  // Main role: show co-label when its co-role has a value
                  const coRoleCode = `Co${role.roleCode}`;
                  const coRole = rolesDefs.find(r => r.roleCode === coRoleCode);
                  const coAssignments = coRole ? assignments.filter(a => a.roleId === coRole.roleId) : [];
                  if (coAssignments.some(a => a.userName)) {
                    displayLabel = coRoleCode
                      .replace(/^Co([A-Z])/, 'Co-$1')
                      .replace(/([a-z])([A-Z])/g, '$1 $2')
                      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
                  } else {
                    displayLabel = label;
                  }
                }

                return (
                  <ReadOnlyField
                    key={role.roleId}
                    label={displayLabel}
                    values={userNames.length > 0 ? userNames : undefined}
                  />
                );
              })}
            </div>
          ));
        })()}
      </div>

      {/* Back Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '10px 28px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#0078d4',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '0.95rem',
            fontWeight: 600,
          }}
        >
          BACK
        </button>
      </div>
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  primary: _primary = false,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 24px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#0078d4',
        color: '#fff',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '0.9rem',
        letterSpacing: '0.5px',
        transition: 'background-color 0.2s',
      }}
    >
      {label}
    </button>
  );
}
